import React from 'react';
import styled from 'styled-components/macro';
import { NewModal } from 'lib/components';
import { VerificationStatus } from 'lib/const/VerificationStatus';
import { CommonTypography } from 'lib/components/styles/typography';
import { theme } from 'lib/style';
import { UserDetailsData } from 'lib/context';
import { Button } from 'react-covideo-common';
import { useBulkUpdateUsersCustomerAndStatus } from 'lib/api/superadmin/useBulkUpdateUsersCustomerAndStatus';
import { useEditUserMutation } from 'lib/api/users/useEditUserMutation';
import { CHANGE_ORIGIN } from 'lib/const/AccountChange';
import { Trans, useTranslation } from 'react-i18next';

const SubmitButtonWrapper = styled.div`
  display: flex;
  gap: 10px;
  justify-content: right;
  margin-top: 32px;
`;

const Title = styled(CommonTypography).attrs({ as: 'p' })`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: ${theme.palette.covideoGray100};
`;

type Props = {
  newStatus: VerificationStatus;
  user: Partial<UserDetailsData>;
  handleModalClose(refresh: boolean): void;
  superAdmin?: boolean;
};

export const UserStatusChangeConfirmationModal = ({
  newStatus,
  user,
  handleModalClose,
  superAdmin = true,
}: Props) => {
  const { t } = useTranslation();
  const isActive = newStatus === VerificationStatus.ACTIVE;

  const closeModal = () => {
    handleModalClose(true);
  };

  const { mutate: updateUserStatus, isLoading: isUpdatingUserStatus } =
    useBulkUpdateUsersCustomerAndStatus(closeModal);
  const { mutateAsync: editUserMutation, isLoading: isEditingUser } =
    useEditUserMutation();

  const loading = isUpdatingUserStatus || isEditingUser;

  const handleUpdateStatus = async () => {
    const id = user?.id;
    if (!id) return;

    const payload = { userIds: [id], status: newStatus };

    return superAdmin
      ? updateUserStatus(payload)
      : editUserMutation({
          data: { active: newStatus },
          changeOrigin: CHANGE_ORIGIN.manageUsers,
          userId: id.toString(),
        });
  };

  return (
    <NewModal
      headerText={`${isActive ? t('Activate') : t('Deactivate')} '${
        (user.firstName || '') + ' ' + (user.lastName || '')
      }'?`}
      hideHeader={false}
      closeModalOnBackdropOrEscPress={true}
      closeModal={() => handleModalClose(false)}
      style={{
        content: {
          minWidth: '592px',
          paddingLeft: '32px',
        },
      }}
    >
      <Title>
        <Trans
          i18nKey='userConfirmStatusChange'
          values={{
            action: isActive ? 'activate' : 'deactivate',
            name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
          }}
        />
      </Title>

      <SubmitButtonWrapper>
        <Button
          variant='secondary'
          onClick={() => handleModalClose(false)}
          text={t('actionsKeepStatus', {
            status: isActive ? t('Inactive') : t('Active'),
          })}
          disabled={loading}
        />
        <Button
          variant='red'
          onClick={() => handleUpdateStatus()}
          text={t('actionsConfirmStatus', {
            action: isActive ? t('activate') : t('deactivate'),
          })}
          disabled={loading}
        />
      </SubmitButtonWrapper>
    </NewModal>
  );
};
